import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import FAQ from './pages/FAQ';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import WhyActivity from './pages/WhyActivity';
import NotFound from './pages/NotFound';
import { BRAND_NAME, DEFAULT_OG_IMAGE, ORGANIZATION_ID, SITE_URL, WEBSITE_ID, TWITTER_HANDLE } from './seo';
import './App.css';

function App() {
  useEffect(() => {
    if (!window.matchMedia('(pointer: fine)').matches) return undefined;

    const cursor = document.createElement('div');
    cursor.className = 'dot-cursor';
    document.body.appendChild(cursor);
    document.body.classList.add('has-dot-cursor');

    const interactiveSelector = 'a, button, [role="button"], input, select, textarea, label, summary';

    const handleMouseMove = (event) => {
      cursor.style.left = `${event.clientX}px`;
      cursor.style.top = `${event.clientY}px`;
    };

    const handleMouseOver = (event) => {
      if (event.target.closest(interactiveSelector)) {
        cursor.classList.add('is-hover');
      }
    };

    const handleMouseOut = (event) => {
      if (event.target.closest(interactiveSelector)) {
        cursor.classList.remove('is-hover');
      }
    };

    const handleMouseDown = () => {
      cursor.classList.add('is-press');
    };

    const handleMouseUp = () => {
      cursor.classList.remove('is-press');
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('has-dot-cursor');
      cursor.remove();
    };
  }, []);

  const defaultHelmetProps = {
    titleTemplate: `%s | ${BRAND_NAME}`,
    defaultTitle: `${BRAND_NAME} - Activity-Specific Breathwork App`,
    htmlAttributes: {
      lang: 'en',
      dir: 'ltr'
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'Merokee Ventures',
    alternateName: 'Apex Breath Developer',
    url: SITE_URL,
    logo: `${SITE_URL}/apex_breath_logo.jpeg`,
    description:
      'Developer of Apex Breath, the activity-specific breathwork app for athletes and wellness enthusiasts.',
    sameAs: [
      'https://instagram.com/ApexBreath',
      'https://x.com/ApexBreath',
      'https://apps.apple.com/us/app/apex-breath/id6743015773'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'troy@merokeeventures.com',
      availableLanguage: 'English'
    }
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: 'Apex Breath',
    alternateName: 'Apex Breath App',
    url: SITE_URL,
    description:
      'Official website for Apex Breath - the activity-specific breathwork app for better health, focus, and athletic performance',
    publisher: {
      '@type': 'Organization',
      '@id': ORGANIZATION_ID
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    },
    inLanguage: 'en-US'
  };

  return (
    <HelmetProvider>
      <Helmet {...defaultHelmetProps}>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#ffffff" />
        <meta
          name="description"
          content="Activity-specific breathwork courses for sport, focus, and recovery. Train your breath with structured sessions in Apex Breath."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="og:image:alt" content="Apex Breath app preview" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={TWITTER_HANDLE} />
        <meta name="twitter:creator" content={TWITTER_HANDLE} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>

      <Router>
        <div className="App">
          <Header />
          <main role="main">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<Terms />} />
              <Route path="/why-activity" element={<WhyActivity />} />
              <Route path="/WhyActivity" element={<WhyActivity />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
